import strings from 'strings';
import { BulkDrawerFormFieldsMap } from 'UI/components/organisms/BulkEmailForm/fields';

import { FeatureFlags } from './featureFlags';

export const AppName = 'FortPac';
export const FORTPAC_SUPPORT_LINK =
  window.GPAC_ENV?.SUPPORT_GLIP_URL || process.env.REACT_APP_SUPPORT_GLIP_URL;

export const TALENT_POOL_URL =
  window.GPAC_ENV?.TALENT_POOL_URL ||
  process.env.REACT_APP_TALENT_POOL_URL ||
  'https://v2.fortpac.com';

export const COURSES_TRAININGS_URL =
  window.GPAC_ENV?.COURSES_TRAININGS_URL ||
  process.env.REACT_APP_COURSES_TRAININGS_URL ||
  'https://training.gogpac.com/enrollments';

export const HELP_ARTICLES_URL =
  window.GPAC_ENV?.HELP_ARTICLES_URL ||
  process.env.REACT_APP_HELP_ARTICLES_URL ||
  'https://gpac.freshservice.com/support/solutions/21000071629';

export const CALL_VERBIAGES_URL =
  window.GPAC_ENV?.CALL_VERBIAGES_URL ||
  process.env.REACT_APP_CALL_VERBIAGES_URL ||
  'https://gogpac.sharepoint.com/sites/TheDen/SitePages/Call-Verbiages-Portal.aspx';

export const drawerAnchor = 'right';

export const usCenterCoordinates = {
  latitude: 39.052136,
  longitude: -94.71583
};

export const DELAY_BOUNCE_TIME = 500;

export const waitingTimeBeforeRedirect = 1000;

/**
 * @typedef {object} DateFormats
 * @property {string} DayAndLargeDate - 'dddd MMMM DD'
 * @property {string} DayDate - 'dddd, MM/DD/YYYY'
 * @property {string} DetailDate - 'MMMM DD, YYYY'
 * @property {string} DetailDateTime - 'MMMM DD, hh:mm A'
 * @property {string} InterviewTemplateDate - 'ddd, MMM Do'
 * @property {string} MonthDay - 'MMM DD'
 * @property {string} MonthDayNumberYear - 'MMMM Do, YYYY
 * @property {string} MonthDayTime - 'MMM DD, hh:mm A'
 * @property {string} MonthDayAtTime - MMM DD [at] hh:mm A
 * @property {string} MonthDayTimeHyphen - 'MMM DD - hh:mm A'
 * @property {string} MonthDayYear - 'MMM DD, YYYY'
 * @property {string} MonthDayYearTime - 'MMM DD, YYYY hh:mm A'
 * @property {string} MonthNumberDay - 'MMMM Do'
 * @property {string} MonthYear - 'MMM YYYY'
 * @property {string} QueryFormat - 'YYYY-MM-DD HH:mm:ss'
 * @property {string} QueryShortFormat - 'YYYY-MM-DD'
 * @property {string} SendoutTemplateDate - 'dddd, MMMM DD, hh:mm A'
 * @property {string} SimpleDate - 'MM/DD/YYYY'
 * @property {string} SimpleDateHyphen - 'MM-DD-YYYY'
 * @property {string} SimpleDateTime - 'MM/DD/YYYY hh:mm A'
 * @property {string} SimpleDateTimeHyphen - 'MM-DD-YYYY hh:mm A'
 * @property {string} SimpleMonthNumberDay - 'MMM Do'
 * @property {string} SimpleTime - 'hh:mm A'
 * @property {string} SimpleTime24Format - 'HH:mm'
 */
export const DateFormats = {
  DateTimeWithTimeZone: 'YYYY-MM-DDTHH:mm:ss.SSSZ',
  DayAndLargeDate: 'dddd MMMM DD',
  DayDate: 'dddd, MM/DD/YYYY',
  DetailDate: 'MMMM DD, YYYY',
  DetailDateTime: 'MMMM DD, hh:mm A',
  InterviewTemplateDate: 'ddd, MMM Do',
  MonthDay: 'MMM DD',
  MonthDayAtTime: 'MMM DD [at] hh:mm A',
  MonthDayNumberYear: 'MMMM Do, YYYY',
  MonthDayTime: 'MMM DD, hh:mm A',
  MonthDayTimeHyphen: 'MMM DD - hh:mm A',
  MonthDayYear: 'MMM DD, YYYY',
  MonthDayYearTime: 'MMM DD, YYYY hh:mm A',
  MonthNumberDay: 'MMMM Do',
  MonthYear: 'MMM YYYY',
  QueryFormat: 'YYYY-MM-DD HH:mm:ss',
  QueryShortFormat: 'YYYY-MM-DD',
  SearchProjectSplit: 'YYYY-MM-DDThh:mm:ss',
  SendoutTemplateDate: 'dddd, MMMM DD, hh:mm A',
  SimpleDate: 'MM/DD/YYYY',
  SimpleDateHyphen: 'MM-DD-YYYY',
  SimpleDateTime: 'MM/DD/YYYY hh:mm A',
  SimpleDateTimeHyphen: 'MM-DD-YYYY hh:mm A',
  SimpleMonthNumberDay: 'MMM Do',
  SimpleTime: 'hh:mm A',
  SimpleTime24Format: 'HH:mm'
};

export const FilterType = {
  Autocomplete: 'autocomplete',
  Text: 'text',
  Select: 'select',
  Search: 'search',
  Input: 'input',
  Switch: 'switch',
  DateRange: 'dateRange',
  Date: 'date',
  Range: 'range',
  AdvancedSearch: 'advancedSearch',
  City: 'city',
  Salary: 'salary',
  PhoneNumber: 'phoneNumber'
};

export const FilterRangeSufix = {
  From: 'From',
  To: 'To'
};

export const HideMode = {
  Hidden: 'hidden',
  Unmounted: 'unmounted'
};

export const GranularityFormats = {
  hour: 'EEE dd, HH aa',
  hour24: 'HH aa',
  day: 'MMM dd',
  week: 'MMM dd, yyyy',
  month: 'MMM yyyy'
};

export const PageTitles = {
  BulkEmail: `Bulk Emails | ${AppName}`,
  Candidate: `Candidates | Inventory | ${AppName}`,
  CandidateCreate: `New Candidate | ${AppName}`,
  CandidateProfile: `${AppName} | Candidate Profile`,
  Collections: `${AppName} | AR & Collections`,
  CommandCenterActivity: `Activity | Command Center | ${AppName}`,
  CommandCenterCommissionsBoard: `Commissions Board | Command Center | ${AppName}`,
  CommandCenterDailyPerformance: `Daily Performance | Command Center | ${AppName}`,
  CommandCenterDominateSheet: `Dominate Sheet | Command Center | ${AppName}`,
  CommandCenterFallOffsBoard: `Fall Offs Board | Command Center | ${AppName}`,
  CommandCenterOverallPerformance: `Overall Performance | Command Center | ${AppName}`,
  CommandCenterOverview: `Overview | Command Center | ${AppName}`,
  CommandCenterProductionBoard: `Production Board | Command Center | ${AppName}`,
  Company: `Companies | Directory | ${AppName}`,
  CompanyCreate: `New Company | ${AppName}`,
  CompanyProfile: `${AppName} | Company Profile`,
  Contact: `Contacts | Directory | ${AppName}`,
  ContactCreate: `New Contact | ${AppName}`,
  ContactProfile: `${AppName} | Contact Profile`,
  DailyPlan: `Daily Plan | ${AppName}`,
  Dashboard: `Inventory | Dashboard | ${AppName}`,
  DashboardBulk: `Bulk Emails | Dashboard | ${AppName}`,
  DashboardFeeAgreementsBoard: `Fee Agreements | Dashboard | ${AppName}`,
  DashboardPhone: `Calls & Texts | Dashboard | ${AppName}`,
  DashboardSendoutBoard: `Sendouts | Dashboard | ${AppName}`,
  DashboardSendoutLeaders: `Leaderboard | Dashboard | ${AppName}`,
  DashboardToolsUsageReport: `Tools Report | Dashboard | ${AppName}`,
  Default: 'FortPac | gpac',
  EditCandidateDataSheet: `${AppName} | Edit Candidate Data Sheet`,
  EditDataSheet: `${AppName} | Edit Data Sheet`,
  FeeAgreements: `Fee Agreements | ${AppName}`,
  HiringAuthority: `${AppName} | Hiring Authority Profile`,
  Home: `Daily Tracker | Command Center | ${AppName}`,
  Industries: `Industries | PIL | ${AppName}`,
  JobOrder: `Job Orders | Inventory | ${AppName}`,
  JobOrderCreate: `New Job Order | ${AppName}`,
  JobOrderProfile: `${AppName} | Job Order Profile`,
  Login: `${AppName} | Login`,
  Map: `Map | PIL | ${AppName}`,
  NotFound: `${AppName} | Not Found`,
  Placements: `Placements | Inventory | ${AppName}`,
  Roleplay: `AI Roleplay | ${AppName}`,
  Roster: `Roster | ${AppName}`,
  SearchProject: `Search Project | ${AppName}`,
  SearchProjectPreview: `${AppName} | Search Project Preview`,
  Sendouts: `Hot Sheet | Inventory | ${AppName}`,
  SendoutsFromChannelPartner: `Channel Partner HS | Inventory | ${AppName}`,
  Sourcing: `Sourcing | ${AppName}`,
  Teams: `Teams | PIL | ${AppName}`,
  ResumeSearch: `${strings.resumeSearch.title} | Inventory | ${AppName}`
};

export const DrawerName = {
  Notifications: 'notifications',
  OperatingMetrics: 'metrics',
  FeeAgreements: 'feeagreement'
};

export const RecruiterBarMode = {
  Compact: 'compact',
  Large: 'large'
};

export const DefaultProfile = {
  id: null,
  title: null,
  name: null,
  contact: null,
  personalInformation: null,
  specialty: null,
  subspecialty: null,
  position: null,
  email: null,
  current_company: null,
  sourceType: null,
  recruiter: null,
  coach: null,
  additionalRecruiters: [],
  company: null,
  address: null,
  source: null,
  signed: null,
  website: null,
  free_game: false
};

export const NotificationType = {
  Inventory: 'inventory',
  OperatingMetrics: 'operating_metrics',
  FeeAgreement: 'fee_agreement',
  SuccessRules: 'success_rules',
  Social: 'social'
};

export const NotificationsVisibilityOptions = {
  All: 'all',
  Unread: 'unread',
  Read: 'read'
};

export const MaxNumberOfAttachments = 15;

export const defaultMaxFileSize = 26214400;

export const defaultValidExtNames = [
  'csv',
  'doc',
  'docx',
  'jpeg',
  'jpg',
  'pdf',
  'png',
  'ppt',
  'pptx',
  'xls',
  'xlsx'
];

export const ExpandedTreeView = ['mine'];

export const BulkMenuItems = {
  Sent: 'sent',
  Template: 'template',
  Drafts: 'drafts',
  OptOut: 'optout',
  // TODO: should remove create template and template edition from this const
  CreateTemplate: 'createTemplate',
  Schedule: 'schedule',
  TemplateEdition: 'templateEdition'
};

export const BulkScopes = {
  Marketing: 1,
  Recruiting: 2,
  General: 3,
  ById: {
    1: 'Marketing',
    2: 'Recruiting',
    3: 'General'
  },
  keys: {
    marketing: 'Marketing',
    recruiting: 'Recruiting',
    general: 'General'
  }
};

export const BulkMarketingOptions = [
  {
    label: strings.bulkEmails.forms.marketingOptions.blockCompanies,
    name: BulkDrawerFormFieldsMap.BlockCompanies.key,
    defaultValue: true,
    position: 1
  },
  {
    label: strings.bulkEmails.forms.marketingOptions.blockAssociatedCompanies,
    name: BulkDrawerFormFieldsMap.BlockAssociatedCompanies.key,
    defaultValue: true,
    position: 2
  },
  {
    label: strings.bulkEmails.forms.marketingOptions.blockSigned,
    name: BulkDrawerFormFieldsMap.BlockSigned.key,
    position: 3
  },
  {
    label: strings.bulkEmails.forms.marketingOptions.allowMarketingCandidates,
    name: BulkDrawerFormFieldsMap.AllowMarketingCandidates.key,
    position: 4
  }
];

export const registerMultipleCheckboxOptions = ({
  options,
  register,
  shouldSetDefaultValue = false
}) => {
  options.forEach(option => {
    shouldSetDefaultValue
      ? register({ name: option.name, value: option.defaultValue || false })
      : register({ name: option.name });
  });
};

export const BulkRecruitingOptions = [
  {
    label: strings.bulkEmails.forms.recruitingOptions.allowRecruitHirings,
    name: BulkDrawerFormFieldsMap.AllowRecruitHirings.key
  }
];

export const LimitLocalDataOfSelectors = {
  zip: 200,
  city: 500
};

export const SignTypes = {
  signed: 0,
  client: 1,
  notSigned: 2,
  ById: {
    0: 'signed',
    1: 'client',
    2: 'not signed'
  }
};
export const RingCentralLogTypes = {
  Call: {
    id: 1,
    title: 'Call'
  },
  Text: {
    id: 2,
    title: 'Text'
  }
};

export const RingCentralOriginActions = {
  Default: 'Default',
  MultiplePhonesDropdown: 'MultiplePhonesDropdown'
};

export const ActivityLogTypes = {
  BulkEmail: 0,
  Email: 1,
  SMS: 2,
  Call: 3,
  Sendout: 4,
  JobPosting: 5,
  CheckPersonalInventory: 6,
  CheckOfficeInventory: 7,
  CheckPCRAndFortPacInventory: 8,
  CreatePresentation: 9,
  SendInmails: 11,
  CreateRollOrAddRollUp: 12,
  CreateCallPlan: 13,
  GeneralUpdate: 14,
  PendingOffer: 15,
  OfferSent: 16,
  OfferAccepted: 17,
  ReferencesCompleted: 18,
  ReferencesReleaseFormSent: 19,
  Interview: 20,
  Sendover: 21,
  ZoomInfoEmail: 22,
  LinkedinMessages: 23,
  AlertsSetUp: 24,
  LinkedinStatusUpdate: 25,
  AutomaticCall: 26,
  AutomaticText: 27,
  InMailReply: 59
};

export const FeeAgreementDefaultValues = {
  FeePercent: 33.33,
  GuaranteeDays: 30
};

export const FeeAgreementFileCategoryId = 14;

const resolveEnvFlag = (runtimeVal, buildVal, fallback = true) => {
  const raw = runtimeVal !== undefined && runtimeVal !== '' ? runtimeVal : buildVal;
  return raw == null || raw === '' ? fallback : String(raw) === 'true';
};

/**
 * Correct API URL
 * works in dev & prod environments
 */
export const EnvApiUrls = {
  MAIN: `${(window.GPAC_ENV && window.GPAC_ENV.API_URL) || process.env.REACT_APP_API_URL}`,
  WS: `${(window.GPAC_ENV && window.GPAC_ENV.API_WS_URL) || process.env.REACT_APP_API_WS_URL}`,
  MAIN_VERSIONED: window.GPAC_ENV?.API_URL_VERSIONED || process.env.REACT_APP_API_URL_VERSIONED,
  API_URL_AWS: window.GPAC_ENV?.SECONDARY_API_URL || process.env.REACT_APP_SECONDARY_API_URL,
  API_KEY_AWS: window.GPAC_ENV?.SECONDARY_API_KEY || process.env.REACT_APP_SECONDARY_API_KEY,
  SEND_API_KEY: resolveEnvFlag(window.GPAC_ENV?.SEND_API_KEY, process.env.REACT_APP_SEND_API_KEY),
  GPAC_CDN_URL: window.GPAC_ENV?.GPAC_CDN_URL || process.env.REACT_APP_GPAC_CDN_URL,
  ALT_API_URL: window.GPAC_ENV?.ALT_API_URL || process.env.REACT_APP_ALT_API_URL
};

export const WebSocketNamespaces = {
  BulkEmail: '/bulk-email',
  PhoneActivityLog: '/phone-activity-log',
  DashboardPhoneLoading: '/dashboard-phone-loading'
};

export const PreviewTypes = {
  BulkEmail: 'bulk-email-preview'
};

export const activityAvatars = Object.keys(ActivityLogTypes);

export const RowActions = Object.freeze({
  Add: 'add',
  ApplySearch: 'applySearch',
  Call: 'call',
  Delete: 'delete',
  Download: 'download',
  Edit: 'edit',
  Email: 'email',
  Restore: 'restore',
  SendReminder: 'sendReminder',
  Share: 'share',
  Text: 'text',
  View: 'view'
});

export const CommunicationActionPreset = [
  {
    name: RowActions.Call,
    value: 'phone'
  },
  {
    name: RowActions.Text,
    value: 'phone'
  },
  {
    name: RowActions.Email,
    value: 'email'
  }
];

export const SearchProjectPrivacyScopes = {
  private: {
    id: 'private',
    title: 'Private',
    value: true
  },
  public: {
    id: 'public',
    title: 'Public',
    value: false
  },
  getPrivacyState(isPrivate) {
    return isPrivate ? this.private : this.public;
  }
};

export const SelectionType = {
  Exclude: 'exclude',
  Include: 'include'
};

export const DefaultNewCandidateFileCategories = [
  {
    id: 4,
    title: 'Resume'
  },
  {
    id: 17,
    title: 'Cover Letter'
  },
  {
    id: 15,
    title: 'Portfolio'
  },
  {
    id: 1,
    title: 'Candidate Data Sheet'
  }
];

export const defaultProfileColumnsConfig = {
  display: true,
  sort: false
};

export const DefaultNewJobOrderFileCategories = [
  {
    id: 2,
    title: 'Assignment Data Sheet'
  },
  {
    id: 18,
    title: 'Job Description'
  },
  {
    id: 19,
    title: 'Other Files'
  }
];

export const SeverityOptions = {
  error: 'error',
  success: 'success',
  warning: 'warning'
};

export const YesNo = {
  Yes: 'yes',
  No: 'no'
};

export const TypeHistoryItem = {
  Custom: 'custom',
  Sendout: 'sendout'
};

export const Yes = {
  id: '1',
  title: 'Yes'
};
export const No = {
  id: '0',
  title: 'No'
};

export const BinaryOptions = [Yes, No];

export const MarkerTypeClass = {
  0: 'marker-type-alpha',
  1: 'marker-type-poejo',
  2: 'marker-type-cant-help'
};

export const MarkerCompanyClass = {
  0: 'marker-company-signed',
  1: 'marker-company-client',
  2: 'marker-company-not-signed'
};

export const StringsBooleanMap = {
  true: true,
  false: false,
  1: true,
  0: false
};

export const COMPANY_RELATIONSHIP_TYPES = {
  PARENT: 'PARENT',
  CHILD: 'CHILD',
  SIBLING: 'SIBLING'
};

export const COMPANY_RELATIONSHIP_KEY = {
  parentCompany: 'parentCompany',
  childCompanies: 'childCompanies',
  siblingCompanies: 'siblingCompanies'
};

export const USER_FILTER_IDS = {
  Mine: 0,
  MyIndustries: 1,
  MyTeam: 2,
  All: 3,
  MyCollaborations: 4,
  FreeGame: 5,
  MyInventory: 6,
  MyRegion: 7
};

export const OPT_OUT_COLUMNS = ['opt_out_date', 'opt_out_reason', 'is_opted_out'];

export const FP_SECTIONS = {
  Bulk: 'bulk',
  Profile: 'profile',
  Map: 'map',
  EntityCreation: 'entityCreation',
  QuickView: 'quickView'
};

export const BULK_RESTRICTION_RULE = {
  defaultRestriction: 'defaultRestriction',
  noQuotaAvailable: 'noQuotaAvailable',
  quotaNearlyReached: 'quotaNearlyReached',
  scheduleCloseNearScheduled: 'scheduleCloseNearScheduled',
  scheduleCloseNearSent: 'scheduleCloseNearSent',
  sendNearSent: 'sendNearSent',
  sendNearScheduled: 'sendNearScheduled'
};

export const BULK_RESTRICTION_IDS = {
  noQuotaAvailable: 'bulk:sendNow:noQuotaAvailable',
  scheduleCloseNearScheduled: 'bulk:schedule:scheduleHourRange',
  scheduleCloseNearSent: 'bulk:schedule:sentBulkHourRange',
  sendNearSent: 'bulk:sendNow:sentBulkHourRange',
  sendNearScheduled: 'bulk:sendNow:scheduleHourRange'
};

export const COLUMNS_TO_REMOVE_BY_FEATURE_FLAG = {
  OPT_OUT: {
    columnsToRemove: OPT_OUT_COLUMNS,
    featureFlag: FeatureFlags.OptOutFilter
  },
  EMAIL_VALIDATION: {
    columnsToRemove: ['is_valid_email'],
    featureFlag: FeatureFlags.EmailValidationColumn
  },
  PHONES: {
    columnsToRemove: ['phone', 'mobile'],
    featureFlag: FeatureFlags.CandidatesMultiplePhones
  }
};

export const EMAIL_VALIDATION_STATUSES = {
  RISKY: 'risky',
  INVALID: 'invalid',
  VALID: 'valid'
};

export const ARCHIVE_AVAILABILITY_DAYS = {
  bulkTemplate:
    process.env.REACT_APP_TEMPLATE_ARCHIVE_RETENTION_DAYS ||
    window.GPAC_ENV?.TEMPLATE_ARCHIVE_RETENTION_DAYS ||
    30,
  bulkTemplateFolder:
    process.env.REACT_APP_TEMPLATE_FOLDER_ARCHIVE_RETENTION_DAYS ||
    window.GPAC_ENV?.TEMPLATE_FOLDER_ARCHIVE_RETENTION_DAYS ||
    30
};

export const MY_TEMPLATES_FOLDER_ID = 'Mine';
export const ARCHIVE_TEMPLATES_FOLDER_NAME = 'Archived';

export const TEMPLATE_ENTITY_TYPES = {
  Root: 'root',
  Folder: 'folder',
  Template: 'template'
};

export const TREE_VIEW_ENTITY_TYPES = {
  folder: 'Folder',
  item: 'Item'
};

export const DEFAULT_SEARCH_PROJECT_FOLDERS_EXCLUDE_IDS = [
  'MyCollaborations',
  'AllSearchProjects',
  'Archived'
];

export const SEARCH_PROJECT_ROOT_FOLDERS = {
  mine: { id: 'MySearchProjects', name: strings.searchProjects.mainPage.filtersTitles.mine },
  archived: { id: 'Archived', name: strings.searchProjects.mainPage.filtersTitles.archived }
};

export const TargetCompaniesOptions = [
  { id: true, title: strings.companies.creation.targetCompany.options.showTargetCompanies },
  { id: false, title: strings.companies.creation.targetCompany.options.excludeTargetCompanies }
];

export const CompanyAssociationsOptions = [
  { id: true, title: strings.companies.associations.main },
  { id: false, title: strings.companies.associations.branches }
];

export const SEARCH_PROJECT_ITEM_TYPE_IDS = {
  searchProject: 10
};

export const MULTIPLE_SELECTION_TYPES = {
  all: 'all',
  page: 'page',
  unselectAll: 'unselectAll'
};

export const MONTHS = [
  {
    id: 1,
    extendedId: '01',
    title: 'January',
    shortTitle: 'Jan'
  },
  {
    id: 2,
    extendedId: '02',
    title: 'February',
    shortTitle: 'Feb'
  },
  {
    id: 3,
    extendedId: '03',
    title: 'March',
    shortTitle: 'Mar'
  },
  {
    id: 4,
    extendedId: '04',
    title: 'April',
    shortTitle: 'Apr'
  },
  {
    id: 5,
    extendedId: '05',
    title: 'May',
    shortTitle: 'May'
  },
  {
    id: 6,
    extendedId: '06',
    title: 'June',
    shortTitle: 'Jun'
  },
  {
    id: 7,
    extendedId: '07',
    title: 'July',
    shortTitle: 'Jul'
  },
  {
    id: 8,
    extendedId: '08',
    title: 'August',
    shortTitle: 'Aug'
  },
  {
    id: 9,
    extendedId: '09',
    title: 'September',
    shortTitle: 'Sep'
  },
  {
    id: 10,
    extendedId: '10',
    title: 'October',
    shortTitle: 'Oct'
  },
  {
    id: 11,
    extendedId: '11',
    title: 'November',
    shortTitle: 'Nov'
  },
  {
    id: 12,
    extendedId: '12',
    title: 'December',
    shortTitle: 'Dec'
  }
];

export const DEFAULT_COUNTRY_CODE = '+1';

export const BULK_METRIC_STATUSES = {
  automaticReply: 'automatic reply',
  bounce: 'bounce',
  click: 'click',
  delivered: 'delivered',
  dropped: 'dropped',
  fixable: 'fixable',
  invalid: 'invalid',
  open: 'open',
  pending: 'pending',
  quotaExceeded: 'quota-exceeded',
  reply: 'reply',
  spam: 'spam',
  undelivered: 'undelivered',
  unreachable: 'unreachable',
  unsubscribe: 'unsubscribe'
};

export const SEARCH_PROJECT_STATUSES = {
  completed: 1,
  inProgress: 2
};

export const AI_ROLEPLAY_CANDIDATE_NAME =
  window.GPAC_ENV?.AI_ROLEPLAY_CANDIDATE_NAME ||
  process.env.REACT_APP_AI_ROLEPLAY_CANDIDATE_NAME ||
  'Alex Smith';

export const AI_ROLEPLAY_HIRING_AUTHORITY_NAME =
  window.GPAC_ENV?.AI_ROLEPLAY_HIRING_AUTHORITY_NAME ||
  process.env.REACT_APP_AI_ROLEPLAY_HIRING_AUTHORITY_NAME ||
  'Jordan Jones';

export const AI_ROLEPLAY_COMPANY =
  window.GPAC_ENV?.AI_ROLEPLAY_COMPANY ||
  process.env.REACT_APP_AI_ROLEPLAY_COMPANY ||
  'ABC Company';

export const BoolEnum = {
  True: 'T',
  False: 'F'
};

export const BoolDescription = {
  [BoolEnum.True]: strings.shared.ui.yes,
  [BoolEnum.False]: strings.shared.ui.no
};

export const SEARCH_PROJECT_SOURCES = {
  matchMaking: {
    id: 1,
    title: 'Match Making'
  },
  talentPool: {
    id: 2,
    title: 'Talent Pool'
  },
  coresignal: {
    id: 3,
    title: 'Coresignal'
  }
};

export const MULTIPLE_ACTIVITY_CREATION_SECTIONS = {
  activities: strings.inventory.shared.listing.multipleActivityCreation.sections.activities,
  keywords: strings.inventory.shared.listing.multipleActivityCreation.sections.keywords,
  notes: strings.inventory.shared.listing.multipleActivityCreation.sections.notes
};
